import HeadingInspector from '@/components/HeadingInspector'
import { lightTheme } from '@/customTheme'
import LanguageMenu from '../LanguageMenu'
import AIUsageMenu from './AIUsageMenu'
import { customAmplitude } from '@/utils/customAmplitude'
import { selectUser } from '@/store/slices/user/selectors'
import { useSelector } from 'react-redux'
import { PremiumOnPlatformType, SignInManager } from '../SignInManager'
import type { UserInfo } from '@/common/interfaces'
import { useTranslation } from 'react-i18next'
import useAppContext from '@/hooks/useAppContext'
import useModal from '@/hooks/useModal'
import { ShowModalType } from '@/scenes/engine/common/constants'
import { useAppDispatch } from '@/store/store'
import { setCallbackFromCreationPage } from '@/store/slices/editor/action'
import { ActionType } from '@/store/slices/editor/actionTypes'
import { useHistory, useLocation } from 'react-router-dom'
import { handleCurrentState, setShowedRemoveObjectTutorial } from '@/utils/tutorial'
import { useEditorContext } from '@/scenes/engine'
import Icons from '../../../Icons'
import NetworkStatusService from '@/shared/services/NetworkStatusService'
import { SyncManager } from '@/firebase/SyncManager'
import ImageRemoveTutorial from '@assets/images/image-remove-tutorial.jpg'
import { selectIsOpenInspector } from '@/store/slices/editor/selector'
import { VerticalListButton } from '../../../FloatingInspectorButton/SharedComponents/Popover/VerticalListButton'
import { HorizontalSeparator } from '../../../FloatingInspectorButton/SharedComponents/Popover/HorizontalSeparator'
import { useState } from 'react'
import * as Sentry from '@sentry/react'

export default function MiniMenu({
  handleClickContact,
  isOpenContactMenu,
  setIsOpenContactMenu,
  isOpenSelectLanguage,
  setIsOpenSelectLanguage,
  isOpenManageSubscription = null,
  setIsOpenManageSubscription = null,
  isOpenAIUsage = false,
  setIsOpenAIUsage = null,
  setIsOpenModalManage = null,
  hasPremium = false,
  closeMenu = null,
  // When true, the `Try Remove objects` row is hidden. Used by the AI Model
  // and Photoshoot product navbars; the editor menu keeps the row visible.
  hideTryRemoveObjects = false,
}) {
  const user = useSelector(selectUser)
  const { t } = useTranslation()
  const { setIsOpenTutorial, setOpenToast, setIsLoadedJsonSuccess, setPopoverActive } = useAppContext()
  const { setShowModalType } = useModal()
  const dispatch = useAppDispatch()
  const history = useHistory()
  const { editor, activeObject, canvas } = useEditorContext()
  const location = useLocation();
  const isOpenInspector = useSelector(selectIsOpenInspector)
  const [webManageBusy, setWebManageBusy] = useState(false)

  const openWebSubscriptionAction = async (kind: 'update' | 'cancel') => {
    if (!user || user.userPremiumOnPlatform !== PremiumOnPlatformType.web) {
      return
    }
    // Open synchronously so Safari/Firefox keep the user-gesture token alive
    // across the awaited API call. Paddle Billing manage URLs are short-lived
    // and can't be pre-warmed, so we use about:blank as a placeholder.
    const popup = window.open('about:blank', '_blank')
    setWebManageBusy(true)
    try {
      const sm = SignInManager.getInstance()
      await sm.ensureWebManageUrlsLoaded(user.paddleWebSubscriptionId ?? null)
      const fresh = sm.getUserProperties() as UserInfo
      const url = kind === 'update' ? fresh.manageUpdateUrl : fresh.manageCancelUrl
      if (url && popup) {
        popup.location.href = url
      } else {
        popup?.close()
      }
    } catch (e) {
      popup?.close()
      Sentry.captureException(e, { tags: { area: 'paddle-manage-url' } })
    } finally {
      setWebManageBusy(false)
    }
  }

  const onTryRemoveObjectsClick = async () => {
    const isFromCreatePage = location.pathname.includes('/create')
    if (!isFromCreatePage) {
      setPopoverActive(null)
      handleCurrentState(editor.handlers, isFromCreatePage)
      setIsLoadedJsonSuccess(false)
      editor.handlers.objectsHandler.clear(false, false)
      await editor.handlers.objectsHandler.addImageToCanvasByUrl(ImageRemoveTutorial, {
        resizeFrame: true,
        ignoreAnimation: true,
        isFlushed: editor.handlers.transactionHandler.isFlushed,
      })
      setIsLoadedJsonSuccess(true)
      if (isOpenInspector) {
        setTimeout(() => {
          setIsOpenTutorial(true)
        }, 400)
      } else {
        setIsOpenTutorial(true)
      }
      setShowModalType(null)
    } else {
      dispatch(setCallbackFromCreationPage({
        action: ActionType.QUICK_TUTORIAL,
      }))
    }
    setShowedRemoveObjectTutorial()
    location.pathname === '/' ? history.replace('/') : history.push('/')
    if (closeMenu) {
      closeMenu()
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        { !isOpenContactMenu && !isOpenSelectLanguage && !isOpenManageSubscription && !isOpenAIUsage && (
          <>
            {hasPremium && (
              <VerticalListButton
                variant="menu"
                text={t('Manage subscription')}
                onClick={() => {
                  setIsOpenManageSubscription(true)
                  customAmplitude('Settings - Manage Subscription')
                }}
              />
            )}
            {hasPremium && (
              <VerticalListButton
                variant="menu"
                text={t('AI Credits')}
                onClick={() => setIsOpenAIUsage(true)}
              />
            )}
            <VerticalListButton
              variant="menu"
              text={t('Language')}
              onClick={() => setIsOpenSelectLanguage(true)}
            />
            {!hideTryRemoveObjects && (
              <VerticalListButton
                variant="menu"
                text={t('Try Remove objects')}
                onClick={onTryRemoveObjectsClick}
              />
            )}
            <VerticalListButton
              variant="menu"
              text={t('Get the app')}
              onClick={() => {
                customAmplitude('Get App Modal', { Source: 'settings'})
                setShowModalType(ShowModalType.DOWNLOAD_APP)
                closeMenu()
              }}
            />
            <VerticalListButton
              variant="menu"
              text={t('Contact us')}
              onClick={() => setIsOpenContactMenu(true)}
            />
            <VerticalListButton
              variant="menu"
              text={t("What's new")}
              disabled={!SyncManager.shared.hasConnection}
              onClick={async () => {
                const isOnline = await NetworkStatusService.getInstance().getLatestStatusConnection()
                if(!isOnline) {
                  setOpenToast('noInternet')
                } else {
                  setShowModalType(ShowModalType.WHAT_NEW)
                  closeMenu()
                  customAmplitude('Whats New Prompt', {
                    Source: 'Settings'
                  })
                }
              }}
            />
          </>
        )}
        {isOpenContactMenu && (
          <>
            <HeadingInspector
              hasNavigation={true}
              handleNavigation={() => setIsOpenContactMenu(false)}
              title={t('Contact us')}
              style={{
                padding: '0 0 16px 0'
              }}
            ></HeadingInspector>
            <VerticalListButton
              variant="menu"
              text={t('I have a suggestion')}
              onClick={() => handleClickContact()}
            />
            <VerticalListButton
              variant="menu"
              text={t('Ask us anything')}
              onClick={() => handleClickContact()}
            />
          </>
        )}
        {isOpenSelectLanguage && <LanguageMenu handleNavigation={() => setIsOpenSelectLanguage(false)} />}
        {isOpenAIUsage && (
          <AIUsageMenu
            handleNavigation={() => setIsOpenAIUsage(false)}
            closeMenu={closeMenu}
          />
        )}
        {isOpenManageSubscription && (
          <>
            <HeadingInspector
              hasNavigation={true}
              handleNavigation={() => setIsOpenManageSubscription(false)}
              title={t('Manage subscription')}
              style={{
                padding: '0 0 16px 0'
              }}
            ></HeadingInspector>
            <div style={{
              height: '72px',
              width: '100%',
              backgroundColor: lightTheme.colors.grayScale50,
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'row',
              gap: '8px',
              padding: '12px',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <div style={{flex: 1}}>
                <h5 style={{...lightTheme.typography.Small14Bold, ...(user.userIsSuper ? lightTheme.typography.LinearGradientTextSuperBrand : lightTheme.typography.LinearGradientText )}}>{user.userIsSuper ? t('You are Super') : t('You are Premium')}</h5>
                <p onClick={() => window.open('https://support.bazaart.com/en/content/what-s-included-in-bazaart-premium', '_blank')} style={{...lightTheme.typography.Small14regular, textDecoration: 'underline', cursor: 'pointer'}}>{t('Learn more')}</p>
              </div>
              <div style={{ width: '32px', height: '32px', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <Icons.NowPremiumIcon />
              </div>
            </div>
            <HorizontalSeparator style={{ margin: '20px 0' }} />
            <VerticalListButton
              variant="menu"
              text={t('Update payment method')}
              disabled={webManageBusy}
              onClick={() => {
                if (user.userPremiumOnPlatform === PremiumOnPlatformType.web) {
                  void openWebSubscriptionAction('update')
                } else {
                  // ios, android, or null (e.g., post-upgrade race) -> mobile-app modal.
                  setIsOpenModalManage(true)
                }
              }}
            />
            <VerticalListButton
              variant="menu"
              text={t('Cancel subscription')}
              disabled={webManageBusy}
              onClick={() => {
                // @ts-ignore
                window.dataLayer.push({ event: 'cancel_subscription'});

                if (user.userPremiumOnPlatform === PremiumOnPlatformType.web) {
                  void openWebSubscriptionAction('cancel')
                } else {
                  // ios, android, or null (e.g., post-upgrade race) -> mobile-app modal.
                  setIsOpenModalManage(true)
                }
              }}
            />
          </>
        )}
    </div>
  )
}